/**
 * Hacienda Status Card
 *
 * Displays Hacienda electronic invoicing status and submission history.
 */

import { useState } from 'react'
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  FileJson,
  FileText,
  FileCode,
  Loader2,
} from 'lucide-react'

import { formatDateTime, cn } from '@/lib/utils'
import api from '@/lib/api'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/components/ui/toaster'
import {
  BillDetail,
  SubmissionAttempt,
  HACIENDA_STATUS_LABELS,
  HACIENDA_STATUS_COLORS,
  HaciendaStatus,
} from '@/types'
import {
  useInvoiceDetails,
  useCreditNoteDetails,
  useTicketDetails,
  hasFinalHaciendaStatus,
} from '@/hooks/useBills'
import { BillType } from '@/types'
import { FriendlyErrors, formatJson } from '@/pages/settings/incidentShared'

interface HaciendaStatusCardProps {
  bill: BillDetail
  submissions: SubmissionAttempt[]
  onResend?: () => void
  isResending?: boolean
}

export function HaciendaStatusCard({
  bill,
  submissions,
  onResend,
  isResending,
}: HaciendaStatusCardProps) {
  const { toast } = useToast()
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [expandedAttempt, setExpandedAttempt] = useState<number | null>(null)

  // v4.4 hooks

  // Use Alanube document ID for API queries
  const documentId = bill.alanube_document_id
  const documentKey = documentId || bill.clave_hacienda

  // Determine document type to use the correct API endpoint
  const isCreditNote = bill.bill_type === BillType.CREDIT_NOTE
  const isTicket = bill.bill_type === BillType.TICKET

  // Detalle del servicio (estado y enlaces de PDF/XML). Con veredicto final no
  // se pide al abrir: los enlaces se piden al hacer clic.
  const detailsOptions = {
    auto: !hasFinalHaciendaStatus(bill.hacienda_status),
    knownStatus: bill.hacienda_status,
  }
  const {
    data: invoiceDetailsData,
    isLoading: isLoadingInvoice,
    refetch: refetchInvoice
  } = useInvoiceDetails(!isCreditNote && !isTicket ? documentId : null, detailsOptions)

  const {
    data: creditNoteDetailsData,
    isLoading: isLoadingCreditNote,
    refetch: refetchCreditNote
  } = useCreditNoteDetails(isCreditNote ? documentId : null, detailsOptions)

  const {
    data: ticketDetailsData,
    isLoading: isLoadingTicket,
    refetch: refetchTicket
  } = useTicketDetails(isTicket ? documentId : null, detailsOptions)

  // Use the appropriate data based on document type
  const invoiceDetails = isCreditNote
    ? creditNoteDetailsData
    : isTicket
      ? ticketDetailsData
      : invoiceDetailsData
  const isLoadingDetails = isCreditNote
    ? isLoadingCreditNote
    : isTicket
      ? isLoadingTicket
      : isLoadingInvoice
  const refetchDetails = isCreditNote
    ? refetchCreditNote
    : isTicket
      ? refetchTicket
      : refetchInvoice


  const [isSyncing, setIsSyncing] = useState(false)

  // El backend pide el enlace en el momento (vencen) y devuelve el archivo.
  const [downloading, setDownloading] = useState<'pdf' | 'xml' | null>(null)
  const downloadDocument = async (kind: 'pdf' | 'xml') => {
    setDownloading(kind)
    try {
      const response = await api.get(`/invoicing/documents/file/${bill.id}/${kind}`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `${bill.clave_hacienda || bill.id}.${kind}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      toast({ title: t('invoicing.downloadError'), variant: 'destructive' })
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadPdf = () => downloadDocument('pdf')
  const handleDownloadXml = () => downloadDocument('xml')

  const handleSyncStatus = async () => {
    setIsSyncing(true)
    try {
      await refetchDetails()
      toast({ title: t('invoicing.statusUpdated') })
    } catch {
      toast({ title: t('invoicing.syncError'), variant: 'destructive' })
    } finally {
      setIsSyncing(false)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: `${label} ${t('common.copied')}` })
  }

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'accepted':
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
      case 'rejected':
      case 'failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
      case 'retrying':
        return <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
      case 'submitted':
        return <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
      default:
        return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
    }
  }

  const getStatusColor = (status?: string) => {
    return HACIENDA_STATUS_COLORS[status as HaciendaStatus] || 'bg-gray-100 text-gray-800'
  }

  const hasHaciendaData = documentKey || bill.consecutivo_hacienda || bill.hacienda_status

  // If no Hacienda data and no submissions, show a minimal card
  if (!hasHaciendaData && submissions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileJson className="h-4 w-4" />
            {t('bills.haciendaStatus')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4 text-muted-foreground">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span className="text-sm">{t('bills.notSubmittedToHacienda')}</span>
          </div>
          {onResend && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={onResend}
                disabled={isResending}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', isResending && 'animate-spin')} />
                {t('bills.sendToHacienda')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileJson className="h-4 w-4" />
          {t('bills.haciendaStatus')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge - Use invoice details status if available */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLoadingDetails ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              getStatusIcon(invoiceDetails?.hacienda_status || bill.hacienda_status)
            )}
            <Badge className={getStatusColor(invoiceDetails?.hacienda_status || bill.hacienda_status)}>
              {invoiceDetails?.hacienda_status
                ? HACIENDA_STATUS_LABELS[invoiceDetails.hacienda_status as HaciendaStatus] || invoiceDetails.hacienda_status.toUpperCase()
                : bill.hacienda_status
                  ? HACIENDA_STATUS_LABELS[bill.hacienda_status as HaciendaStatus] || bill.hacienda_status
                  : t('bills.pending')}
            </Badge>
          </div>
          {onResend && (bill.hacienda_status === 'failed' || bill.hacienda_status === 'error' || bill.hacienda_status === 'rejected') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResend}
              disabled={isResending}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', isResending && 'animate-spin')} />
              {t('bills.retry')}
            </Button>
          )}
        </div>

        {/* Clave Hacienda */}
        {bill.clave_hacienda && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t('bills.claveHacienda')}</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono bg-muted p-2 rounded flex-1 overflow-x-auto">
                {bill.clave_hacienda}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => copyToClipboard(bill.clave_hacienda!, 'Clave')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Consecutivo Hacienda */}
        {bill.consecutivo_hacienda && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t('bills.consecutivoHacienda')}</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono bg-muted p-2 rounded">
                {bill.consecutivo_hacienda}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => copyToClipboard(bill.consecutivo_hacienda!, 'Consecutivo')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Document Actions - PDF/XML Download, Sync */}
        {(documentId || bill.hacienda_status) && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {/* PDF Download - only show if PDF URL is available */}
            {documentId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={downloading !== null}
              >
                {downloading === 'pdf'
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <FileText className="h-4 w-4 mr-2" />}
                PDF
              </Button>
            )}

            {/* XML Download - only show if XML URL is available */}
            {documentId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadXml}
                disabled={downloading !== null}
              >
                {downloading === 'xml'
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <FileCode className="h-4 w-4 mr-2" />}
                XML
              </Button>
            )}

            {/* Sync Status - show if we have document ID to sync */}
            {documentId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncStatus}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {t('invoicing.syncStatus')}
              </Button>
            )}

          </div>
        )}

        {/* Government Response / Hacienda Message
            For real errors (rejected/failed/error) render parsed errors with
            friendly titles + suggested actions; the raw text stays accessible
            via the "ver mensaje completo" disclosure. For accepted/in-flight
            we keep the existing tone block. */}
        {(() => {
          const rawMessage =
            invoiceDetails?.government_response ||
            invoiceDetails?.hacienda_message ||
            bill.hacienda_message ||
            ''
          const liveStatus = invoiceDetails?.hacienda_status || bill.hacienda_status
          const isError =
            liveStatus === 'rejected' ||
            liveStatus === 'failed' ||
            liveStatus === 'error' ||
            bill.hacienda_status === 'rejected' ||
            bill.hacienda_status === 'failed' ||
            bill.hacienda_status === 'error'
          const parsedErrors = bill.hacienda_errors || []

          if (isError && (parsedErrors.length > 0 || rawMessage)) {
            return <FriendlyErrors errors={parsedErrors} rawMessage={rawMessage || null} />
          }

          if (!rawMessage) return null
          const isAccepted = liveStatus === 'accepted'
          return (
            <div
              className={cn(
                'p-3 rounded-lg text-sm',
                isAccepted
                  ? 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400'
                  : 'bg-yellow-50 dark:bg-yellow-950 text-yellow-600 dark:text-yellow-400'
              )}
            >
              <p className="font-medium mb-1">
                {isAccepted ? t('bills.haciendaAccepted') : t('bills.haciendaResponse')}
              </p>
              <p className="text-xs whitespace-pre-wrap">{rawMessage}</p>
            </div>
          )
        })()}

        {/* Submission History */}
        {submissions.length > 0 && (
          <Collapsible open={historyExpanded} onOpenChange={setHistoryExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between h-auto py-2">
                <span className="text-sm">
                  {t('bills.submissionHistory')} ({submissions.length})
                </span>
                {historyExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 mt-2">
              {submissions.map((attempt) => (
                <div
                  key={attempt.id}
                  className="border rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      setExpandedAttempt(
                        expandedAttempt === attempt.id ? null : attempt.id
                      )
                    }
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(attempt.status)}
                      <span className="text-sm font-medium">
                        {t('bills.attempt')} #{attempt.attempt_number}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(attempt.created_at)}
                      </span>
                      {expandedAttempt === attempt.id ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </div>

                  {expandedAttempt === attempt.id && (
                    <div className="px-3 pb-3 space-y-2">
                      {attempt.error_message && (
                        <div className="p-2 rounded bg-red-50 dark:bg-red-950 text-xs text-red-600 dark:text-red-400">
                          <p className="font-medium">{t('bills.error')}: {attempt.error_code}</p>
                          <p>{attempt.error_message}</p>
                        </div>
                      )}

                      {attempt.request_payload && (
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium">{t('bills.requestPayload')}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation()
                                copyToClipboard(formatJson(attempt.request_payload!), 'Request')
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
                            {formatJson(attempt.request_payload)}
                          </pre>
                        </div>
                      )}

                      {attempt.response_payload && (
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium">{t('bills.responsePayload')}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation()
                                copyToClipboard(formatJson(attempt.response_payload!), 'Response')
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
                            {formatJson(attempt.response_payload)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}

